import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { defaultColors } from 'src/app/classes/widget/chartHandlerV2';
import { Flow } from 'src/app/interfaces/api/flow';
import { Planning } from 'src/app/interfaces/api/planning';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { Zone } from 'src/app/interfaces/api/zone';
import { defaultFlows, excludedFlowNames } from 'src/app/interfaces/shared/defaultFlows';
import { SafePipe } from 'src/app/pipes/safe.pipe';
import { FlowService } from 'src/app/services/api/flows/flow.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { ZoneService } from 'src/app/services/api/zones/zone.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { environment } from 'src/environments/environment';
import { ConfirmComponent } from 'ui-library';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'app-workflows',
    imports: [
        TranslateModule,
        FormsModule,
        CommonModule,
        MatIconModule,
        MatCheckboxModule,
        MatFormFieldModule,
        NgxMatDatepickerInput,
        MatDatepickerToggle,
        MatOptionModule,
        NgxMatDatetimepicker,
        MatTabsModule,
        MatToolbarModule,
        SafePipe,
        NoDataComponent
    ],
    standalone: true,
    templateUrl: './workflows.component.html',
    styleUrl: './workflows.component.scss'
})

export class WorkflowsComponent implements OnInit, OnDestroy {
    @Input() form: any;
    @Output('onUpdate') onUpdate = new EventEmitter<any>();
    @Input() field!: string;

    flowUrl = environment.FLOW_URL;
    apiUrl = environment.API_URL;
    user: any;
    groupInputs: any[] = [];
    theme: string = "dark";
    project!: Project;
    iframeType: boolean = false;
    workflowForm!: FormGroup;
    selectedTabIndex = 0;
    tabs: any = [];
    trackers: Tracker[] = [];
    trackerNames: string[] = [];
    dataDialog: any = [];
    currentProject: any;
    users: any = [];
    tags: any = [];
    tagNames: any = [];
    planningNames: any = [];
    plannings: Planning[] = [];
    units: Unit[] = [];
    unitNames: string[] = [];
    zones: Zone[] = [];
    zoneNames: string[] = [];
    mailBody: any = [];
    flow!: Flow;
    margins: any = [];
    mailingList: any = [];
    allUsers: any = [];
    marginValues: number[] = Array.from({ length: 50 }, (_, i) => i + 1);
    testFlowCompact: any;
    verifTabCompact: boolean = false;
    testSubflow: any;
    FCS_FlowCompact: any;
    FCS_Subflow: any;
    DA_FlowCompact: any;
    DA_Subflow: any;
    compactTab: any = {
        id: uuidv4(),
        label: "Compact",
        disabled: false,
        type: "tab",
        info: "Flows automatiques depuis la page compact"
    };
    role!: string;
    textExplain: string = "Pour utiliser le nom de l'unit, utiliser {{payload.unit.name}}";
    defaultMailBody: string = `Bonjour,
  Il semblerait que le capteur {{payload.unit.name}} soit déconnecté.
  Pensez à vérifier sur https://app.unifield.io/ et à faire la rotation des batteries.
  Bien à vous,
  L'équipe CAD.42`;
    helpLink = "https://cad42.atlassian.net/wiki/x/AwD7IQ";
    inputs: any;
    allDataTemplate: any = [];
    dynamicVariables: any = {};
    destroy$ = new Subject<void>();

    constructor(private auth: AuthService,
        private dialog: MatDialog,
        private projectsService: ProjectService,
        private planningsService: PlanningService,
        private unitsService: UnitService,
        private flowsService: FlowService,
        private zonesService: ZoneService,
        private usersService: UserService,
        private translate: TranslateService,
        private trackersService: TrackerService,
        private pop: Pop,
        private route: ActivatedRoute) {

        this.user = this.auth.currentUser();
        if (this.user) {
            this.theme = this.user.theme;
            this.role = this.user.role.name;

            if (this.user.role.name == 'admin' || this.user.role.name == 'dev') {
                this.iframeType = false;
            } else {
                this.iframeType = true;
            }
        }

        this.projectsService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
            if (this.project && this.project.id != project.id) {
                this.project = project;
                this.ngOnInit();
            } else {
                this.project = project;
            }
        });
    }

    ngOnInit(): void {
        this.allUsers = [];
        let startDate = this.route.snapshot.queryParamMap.get('startDate');
        let endDate = this.route.snapshot.queryParamMap.get('endDate');
        this.user = this.auth.currentUserValue;

        this.planningsService.getPlannings(this.user.id).pipe(takeUntil(this.destroy$)).subscribe(plannings => {
            if (plannings && plannings.length > 0) {
                this.planningNames = plannings.map(p => p.name);
                this.plannings = plannings;
            }

        });

        let flowPort = null;
        let flowUrl = environment.FLOW_URL;
        let isLocal = /https:/.test(flowUrl) == false;
        if (!isLocal && this.project.services && this.project.services.flowPort) {
            flowPort = this.project.services.flowPort;
        }
        if (!isLocal && this.project.services && this.project.services.flowRealtime) {
            flowUrl = this.project.services.flowRealtime;
        }

        let dates = '';
        if (startDate && endDate) {
            dates = `&startDate=${startDate}&endDate=${endDate}`
        }

        if (flowPort) {
            this.flowUrl = `${flowUrl}:${flowPort}/editor?theme=${this.theme}&userId=${this.user.id}&projectId=${this.project.id}&database=${this.project.database}&accessKey=${this.project.accessKey}&flowAccessKey=${this.project.flowAccessKey}${dates}`

        } else {
            this.flowUrl = `${flowUrl}/editor?theme=${this.theme}&userId=${this.user.id}&projectId=${this.project.id}&database=${this.project.database}&accessKey=${this.project.accessKey}&flowAccessKey=${this.project.flowAccessKey}${dates}`
        }

        this.usersService.getUsers({ projects: this.project.id }).pipe(takeUntil(this.destroy$)).subscribe((results: User[]) => {
            this.users = results.map(r => r.email)
            if (this.project.mailingList) {
                this.mailingList = this.project.mailingList.map((id) => results.find(r => r.id == id)?.email);
            }
            this.allUsers = this.users;
            this.users.push('all');
            this.users.push('mailingList');
        });

        this.trackersService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
            this.trackerNames = trackers.map(tracker => tracker.name);
            this.trackers = trackers;
        });

        this.unitsService.getUnits().pipe(takeUntil(this.destroy$)).subscribe((units: Unit[]) => {
            this.units = units;

            this.unitNames = units.map(p => p.name)
            this.dataDialog = units.map(unit => {
                const unitName = unit.name;
                let trackerName = null;

                if (unit.trackers && typeof unit.trackers[0] == 'object') {
                    trackerName = unit.trackers[0].name;
                }

                return {
                    unitName,
                    trackerName
                };
            });

            this.zonesService.getZones().pipe(takeUntil(this.destroy$)).subscribe(zones => {
                if (zones && zones.length > 0) {
                    this.zoneNames = zones.map(p => p.name);
                    this.zones = zones;
                }
            })

            this.flowsService.getLastFlows({}).pipe(takeUntil(this.destroy$)).subscribe(flows => {
                if (flows && flows[0]) {
                    this.flow = flows[0];
                    let compactTab = this.flow.flows.find(f => f.label == "Compact")
                    if (compactTab) {
                        this.compactTab = compactTab;
                    }

                    this.inputs = [];
                    this.tabs = [];
                    this.allDataTemplate = [];
                    if (this.flow && this.flow.templates && this.flow.templates.length > 0) {
                        this.flow.templates.forEach((template) => {
                            const unitName = this.units.find(unit => unit.id == template.unit)?.name;
                            const trackerName = this.trackers.find(tracker => tracker.id == template.tracker)?.name;
                            // const mailBodyValue = template.mailBody ? template.mailBody : this.defaultMailBody;

                            this.tabs.push({ label: { unitName, trackerName, trackerId: template.tracker }, template: template });
                            this.allDataTemplate.push(this.getDataTemplate(template));
                        });

                    }
                }
            })

        })

        this.loadCompactFlowData();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleCompact() {
        if (this.allDataTemplate[0]) {
            this.inputs = this.allDataTemplate[0];
            this.setValues();
        }
    }

    onTabChange(event: any) {
        this.selectedTabIndex = event;
        this.inputs = this.allDataTemplate[event];
        this.setValues();
    }

    getDataTemplate(template: any) {
        let defaultOptions = [...defaultFlows].map(flow => {
            let clonedFlow = JSON.parse(JSON.stringify(flow));

            //Value checkbox flow (disconnectionAlert, craneCycle, planning, collision...)
            if (template[flow.name]) {
                clonedFlow.enabled = true;
            }
            //Value inputs (checkbox, input, text, select...)
            clonedFlow.options = clonedFlow.options.map((input: any) => {
                let clonedInput = JSON.parse(JSON.stringify(input));
                if (input.type == "text" || input.type == "textarea" || input.type == "number" || input.type == "radio" || input.type == "checkbox") {
                    clonedInput.value = template[flow.name] ? template[flow.name][input.name] : input.default;
                } else if (input.type == "select") {
                    clonedInput.value = template[flow.name] ? template[flow.name][input.name] : input.default;
                    //Add options selects
                    if (input.name == "plannings") {
                        clonedInput.options = this.plannings.map(p => {
                            return {
                                name: p,
                                value: p
                            }
                        });
                    } else if (input.name == "users") {
                        clonedInput.options = this.users.map((u: any) => {
                            return {
                                name: u,
                                value: u
                            }
                        });
                    } else if (input.name === "tags") {
                        if (this.project.enableTags)
                            clonedInput.options = this.project.enableTags
                                .filter(tag => tag.enabled) // Filtrer les tags activés uniquement
                                .map(tag => ({
                                    name: tag.name,
                                    value: tag.name
                                }));
                    } else if (input.name == "zones") {
                        clonedInput.options = this.zones.map(z => {
                            return {
                                name: z,
                                value: z
                            }
                        });
                    } else if (input.name == "units") {
                        clonedInput.options = this.unitNames.map(u => {
                            return {
                                name: u,
                                value: u
                            }
                        });
                    } else if (input.name == "margins") {
                        clonedInput.options = this.marginValues.map(m => {
                            return {
                                name: m,
                                value: m
                            }
                        });
                    }
                }
                return clonedInput;
            });

            return clonedFlow;
        });

        return defaultOptions;
    }



    setValues() {
        if (!this.inputs) return;
        this.inputs.forEach((flow: any) => {
            flow.options.forEach((input: any) => {
                if (input.type == "text" || input.type == "number" || input.type == "radio" || input.type == "checkbox") {
                    input.value = input.value !== undefined ? input.value : input.default;
                } else if (input.type == "datetime-local") {
                    // input.value = moment(input.value !== undefined ? input.value : input.default);
                    input.value = input.value !== undefined ? input.value : input.default;
                } else if (input.type == "color") {
                    input.value = input.value !== undefined ? input.value : input.default;
                } else if (input.type == "colors") {
                    if (!input.options) {
                        input.options = defaultColors.map((c: any, i: any) => {
                            return {
                                value: c,
                                label: i
                            };
                        });
                    } else {
                        input.options.sort((a: any, b: any) => {
                            if (isNaN(a.label) && !isNaN(b.label)) {
                                return -1;
                            }
                            if (!isNaN(a.label) && isNaN(b.label)) {
                                return 1;
                            }
                            return 0;
                        });
                    }
                } else if (input.type == "select") {
                    input.value = input.value || input.default;
                    if (input.options) {
                        input.options = input.options.map((o: any) => {
                            if (o && o.value) {
                                return o;
                            } else {
                                return {
                                    name: o,
                                    value: o
                                };
                            }
                        });
                    }
                }
                input.update = (event: any, index = null, type = null) => {
                    if (input.type == "colors" && index && index >= 0) {
                        if (type == 'color') {
                            flow.options[index].value = event.target.value;
                        }
                        if (type == 'label') {
                            flow.options[index].label = event.target.value;
                        }
                    }
                    if (input.type == "datetime-local") {
                        input.value = event.target.value;
                    }
                    if (this.onUpdate) {
                        this.onUpdate.emit({ field: this.field, form: this.form, inputs: [...defaultFlows].concat(this.groupInputs), index });
                    }
                };

                flow.update = (event: any, index = null, type = null) => {
                    flow.enabled = event.checked;
                    if (this.onUpdate) {
                        this.onUpdate.emit({ field: this.field, form: this.form, inputs: [...defaultFlows].concat(this.groupInputs), index });
                    }
                };

            });
        });
    }

    addTab() {
        const availableUnits = this.dataDialog.filter((item: any) => !this.tabs.some((tab: any) => tab.label.unitName === item.unitName) && item.trackerName !== null);
        if (availableUnits.length === 0) {
            return;
        }

        // const dialogRef = this.dialog.open(ListDialogComponent, {
        //     width: '300px',
        //     data: {
        //         title: this.translate.instant('viewer.select_tracker'),
        //         list: availableUnits,
        //     }
        // });

        // dialogRef.afterClosed().subscribe(result => {
        //     if (result) {

        //         const unitId = this.units.find(unit => unit.name == result.unitName)?.id;
        //         const trackerId = this.trackers.find(tracker => tracker.name == result.trackerName)?.id;

        //         const template = {
        //             unit: unitId,
        //             tracker: trackerId,
        //         };

        //         this.tabs.push({ label: result, form: template });

        //         const query = {
        //             id: this.flow.id,
        //             templates: [...this.flow.templates, template]
        //         }

        //         if (this.flow) {
        //             this.flow.templates.push(template)
        //             this.flowsService.updateFlow(query)
        //         }

        //         this.allDataTemplate.push(this.getDataTemplate(template));
        //     }
        // });
    }

    getTabLabel(label: any): string {

        if (label && label.unitName && label.trackerName) {
            return `${label.unitName} (${label.trackerName})`;
        } else if (label && label.unitName) {
            return label.unitName;
        } else if (label && label.trackerName) {
            const dialogDataUnit = this.dataDialog.find((data: any) => data.trackerName == label.trackerName);
            return dialogDataUnit ? `${dialogDataUnit.unitName} (${label.trackerName})` : '';
        } else {
            return '';
        }
    }

    removeTab(index: any) {
        const tabLabel = this.tabs[index].label.unitName;

        const dialogRef = this.dialog.open(ConfirmComponent, {
            width: '300px',
            data: {
                title: this.translate.instant('workflows.delete_tab'),
                content: this.translate.instant('workflows.delete_tab_confirm') + ` "${tabLabel}" ?`,
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result.dismissed) {
                const unitName = this.tabs[index].label.unitName;
                if (this.flow && this.flow.templates) {
                    const templateIndex = this.flow.templates.findIndex(t => {
                        const unit = this.units.find(u => u.id == t.unit);
                        return unit ? unit.name === unitName : false;
                    });

                    if (templateIndex !== -1) {
                        this.flow.templates.splice(templateIndex, 1);

                        for (let indexFlow = this.flow.flows.length - 1; indexFlow >= 0; indexFlow--) {
                            const flow = this.flow.flows[indexFlow];
                            if (flow.type.includes("subflow:") && flow.name.includes(this.tabs[index].label.trackerName)) {
                                this.flow.flows.splice(indexFlow, 1);
                            }
                        }

                        this.checkAllCompactFlows();
                        this.refocusPositionFlows();
                        this.deployFlows();
                    }

                    this.tabs.splice(index, 1);
                }
            }
        });

    }

    queryTemplate() {
        let queryTemplate: any = {}
        if (this.inputs) {
            this.inputs.forEach((input: any) => {
                if (input.enabled) {
                    queryTemplate[input.name] = {};
                    input.options.forEach((option: any) => {
                        queryTemplate[input.name][option.name] = option.value !== undefined ? option.value : option.default;
                    });
                }
            });
        }

        return queryTemplate;
    }

    saveTab(index: number) {
        const selectedTab = this.tabs[index];
        if (!selectedTab) {
            return;
        }
        const templateSave = this.queryTemplate();

        const unitId = this.units.find(unit => unit.name == selectedTab.label.unitName)?.id;
        const trackerId = this.trackers.find(tracker => tracker.name == selectedTab.label.trackerName)?.id;

        let template: any = {
            unit: unitId,
            tracker: trackerId,
            trackerName: this.tabs[index].label.trackerName,
            ...templateSave
        };

        const compactUse = this.flowCompactUse(template);

        if (this.flow && this.flow.templates) {
            this.flow.templates = this.flow.templates.map(t => t.unit === unitId && t.tracker === trackerId ? template : t)
        } else {
            this.flow.templates = [template]
        }

        //suppression de tous les subflow en rapport avec le trackerName
        for (let index = this.flow.flows.length - 1; index >= 0; index--) {
            const flow = this.flow.flows[index];
            if (flow.type.includes("subflow:") && excludedFlowNames.some(name => flow.name.includes(name)) && flow.name.includes(selectedTab.label.trackerName)) {
                this.flow.flows.splice(index, 1);
            }
        }

        ///////////////////////////////////////////////////////////////////////////////////////////
        if (this.flow && this.flow.templates && compactUse) {

            //Vérification si le tab "Compact" existe
            this.verifTabCompact = !!this.flow.flows.find(flow => flow.type === 'tab' && flow.label == 'Compact');
            if (!this.verifTabCompact) {
                this.flow.flows.push(this.compactTab);
            }

            excludedFlowNames.forEach(name => {
                if (template[name]) {
                    // Verifier si le template Subflow est créé dans les flows
                    const verifSubflow = this.flow.flows.filter(flow => flow.type == "subflow" && flow.name == name);
                    if (verifSubflow.length == 0) {
                        this.flow.flows.push(...this.dynamicVariables[name].flows.filter((flow: any) => !flow.type.includes("subflow:")));
                    }

                    //Création des subflow:...
                    let recupSubflow = this.dynamicVariables[name].flows.filter((flow: any) => flow.type.includes("subflow:"));
                    let querySubflow = JSON.parse(JSON.stringify(recupSubflow)); // Copie profonde

                    querySubflow[0].variables.forEach((variable: any) => {
                        if (variable.key == "trackerId") {
                            variable.value = template.tracker;
                        } else if (template[name][variable.key] != undefined) {
                            variable.value = template[name][variable.key];
                        } else if (variable.key == "users") { // Pour les users
                            let users = '';
                            template[name].users.forEach((user: any) => {
                                if (user) {
                                    if (user == 'all' && this.allUsers.length > 0) {
                                        users += this.allUsers.join(',') + ',';
                                    } else if (user == 'mailingList' && this.mailingList.length > 0) {
                                        users += this.mailingList.join(',') + ',';
                                    } else {
                                        users += user + ',';
                                    }
                                }
                            });
                            if (users.length > 0) {
                                users = users.slice(0, -1);
                            }
                            variable.value = users;
                        }
                    });

                    querySubflow[0].id = uuidv4();
                    querySubflow[0].z = this.compactTab.id;
                    querySubflow[0].name = name + "/" + template.trackerName;

                    this.flow.flows.push(querySubflow[0]);
                }
            });
        }

        this.checkAllCompactFlows();
        this.refocusPositionFlows();
        localStorage.setItem('flows', JSON.stringify(this.flow));
        this.deployFlows();
    }

    deployFlows() {
        this.flowsService.deployFlow({ ...this.flow }).subscribe(res => {
            this.pop.toast(this.translate.instant('workflows.deploy_flow'), 'success', 4000, "deploy");
        });
    }

    flowCompactUse(template: any) {
        let compactUse = false;
        excludedFlowNames.forEach(name => {
            if (template[name]) {
                compactUse = true;
            }
        });

        return compactUse;
    }

    loadCompactFlowData() {
        this.flowsService.getCompactFlow().subscribe(result => {
            result.forEach((flow: any) => {
                if (excludedFlowNames.includes(flow.name)) {
                    this.dynamicVariables[flow.name] = flow;
                }
            });
        });
    }

    checkAllCompactFlows() {
        const hasSubflowCompact = this.flow.flows.some(flow => flow.type.includes("subflow:"));

        if (!hasSubflowCompact) {
            const index = this.flow.flows.findIndex(flow => flow.type == "tab" && flow.label == "Compact");
            if (index !== -1) {
                this.flow.flows.splice(index, 1);
            }
        }

        //Vérification si les templates Subflow sont créé dans les flows
        excludedFlowNames.forEach(name => {
            const hasSubflow = this.flow.flows.some(flow => flow.type.includes("subflow:") && flow.name.includes(name));

            if (!hasSubflow) {
                const verifSubflow = this.flow.flows.filter(flow => flow.type == "subflow" && flow.name == name);

                if (verifSubflow.length !== 0) {
                    this.dynamicVariables[name].flows.forEach((subflow: any) => {
                        const index = this.flow.flows.findIndex(flow => flow.id == subflow.id);
                        if (index !== -1) {
                            this.flow.flows.splice(index, 1);
                        }
                    });
                }
            }
        });

        // //Vérification si le template "Subflow FollowCraneStrike" est créé dans les flows
        // const hasFCSSubflow = this.flow.flows.some(flow => flow.type.includes("subflow:") && flow.name.includes("FollowCraneStrike/"));

        // if (!hasFCSSubflow) {
        //   const verifSubflowFCS = this.flow.flows.filter(flow => flow.type == "subflow" && flow.name == "FollowCraneStrike/SubflowCompact");

        //   if (verifSubflowFCS.length !== 0) {
        //     this.FCS_Subflow.forEach(FCSFlow => {
        //       const index = this.flow.flows.findIndex(flow => flow.id === FCSFlow.id);
        //       if (index !== -1) {
        //         this.flow.flows.splice(index, 1);
        //       }
        //     });
        //   }
        // }
    }

    refocusPositionFlows() {
        //Rangement automatique des subflows
        [...defaultFlows].forEach((defaultFlow) => {
            const name = defaultFlow.name;

            if (this.flow && this.flow.flows && this.flow.flows.some(flow => flow.type.includes("subflow:") && flow.name.includes(name))) {
                let SubflowCount = 0;
                this.flow.flows.forEach((flow) => {
                    if (flow.type.includes("subflow:") && flow.name.includes(name)) {
                        flow.y = defaultFlow.y;
                        flow.x = defaultFlow.x + SubflowCount * 300;
                        SubflowCount++;
                    }
                });
            }
        });

        // //Rangement automatique des subflows:FollowCraneStrike
        // if (this.flow && this.flow.flows && this.flow.flows. some(flow => flow.type.includes("subflow:") && flow.name.includes("FollowCraneStrike/"))) {
        //   let FCS_SubflowCount = 0;
        //   this.flow.flows.forEach((flow) => {
        //     if (flow.type.includes("subflow:") && flow.name.includes("FollowCraneStrike/")) {
        //       flow.y = 225.0;
        //       flow.x = 230.0 + FCS_SubflowCount * 300;
        //       FCS_SubflowCount++;
        //     }
        //   });
        // }
    }

}
